import { ChangeDetectorRef, Component, ErrorHandler, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { applyEach, disabled, FieldTree, form, required } from '@angular/forms/signals'

import { MatCheckbox } from '@angular/material/checkbox'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { AsiakkaatDataSourceStateService } from './asiakkaat.datasource.service'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { KirjanpitajanSarake, KirjanpitajanSarakkeenTila } from 'app/_jaettu-lemonator/model/kirjanpitaja'

import { firstValueFrom, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export interface KirjanpitajanSarakkeenMuokkausDialogData {
  kirjanpitajaUid: string
  sarake: KirjanpitajanSarake
}

interface SarakkeenTilaLomake {
  nimi: string
  kuvaus: string
  vari: string | null
  oletus: boolean
}

interface SarakeLomake {
  aktiivinen: boolean
  nimi: string
  tilat: SarakkeenTilaLomake[]
}

@Component({
  templateUrl: './kirjanpitajan-sarakkeen-muokkaus.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitajanSarakkeenMuokkausDialog implements OnInit, OnDestroy {
  @ViewChild('sarakeAktiivinenCheckbox', { read: MatCheckbox, static: true }) sarakeAktiivinenCheckbox: MatCheckbox
  @ViewChild('sarakkeenNimiInput', { read: MatInput, static: true }) sarakkeenNimiInput: MatInput
  @ViewChildren('tilanNimiInput', { read: MatInput }) tilanNimiInputit: QueryList<MatInput>

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  sarake: KirjanpitajanSarake = null
  lomake: FieldTree<SarakeLomake>
  lisaa: boolean = false

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: KirjanpitajanSarakkeenMuokkausDialogData,
    private _dialogRef: MatDialogRef<KirjanpitajanSarakkeenMuokkausDialog>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _errorHandler: ErrorHandler,
    private _copyService: KopioijaPalvelu,
    private _formValidationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _stateService: AsiakkaatDataSourceStateService,
  ) {
    this.sarake = this._data.sarake ? this._copyService.cloneObjectDeep(this._data.sarake) : this.annaUusiSarake()

    this.lisaa = !this.sarake.avain

    this.lomake = form(signal<SarakeLomake>({ aktiivinen: this.sarake.aktiivinen ?? null, nimi: this.sarake.nimi ?? '', tilat: [] }), polku => {
      disabled(polku.nimi, ({ valueOf }) => !valueOf(polku.aktiivinen))
      required(polku.nimi)
      applyEach(polku.tilat, tila => {
        disabled(tila, ({ valueOf }) => !valueOf(polku.aktiivinen))
        required(tila.nimi)
      })
    })
    arvonMuutokset(this.lomake.aktiivinen)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sarake.aktiivinen = !!value
        if (this.sarake.aktiivinen) {
          setTimeout(() => {
            this.sarakkeenNimiInput.focus()
            this._changeDetectorRef.markForCheck()
          }, 10)
        }
      })
    arvonMuutokset(this.lomake.nimi)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sarake.nimi = value
      })

    if (!this.sarake.tilat) {
      this.sarake.tilat = []
    }

    for (const tila of this.sarake.tilat) {
      this._lisaaTilanRivi(tila)
    }
  }

  private annaUusiSarake(): KirjanpitajanSarake {
    const sarake: KirjanpitajanSarake = {
      aktiivinen: true,
      avain: null,
      nimi: '',
      tilat: [this.annaUusiTila()],
      jarjestys: null,
      tallennettu: null,
      tallentanut: null,
    }
    firstValueFrom(this._stateService.kaikkiSarakkeetObservable).then(kaikki => {
      sarake.jarjestys = (kaikki?.length || 0) + 1
    })
    return sarake
  }

  private _lisaaTilanRivi(tila: KirjanpitajanSarakkeenTila) {
    const tilat = this.lomake.tilat().value()
    asetaArvo(this.lomake.tilat, [...tilat, { nimi: tila.nimi ?? '', kuvaus: tila.kuvaus ?? '', vari: tila.vari ?? null, oletus: tila.oletus ?? null }], { ilmoita: false })
    const rivi = this.lomake.tilat[tilat.length]
    arvonMuutokset(rivi.nimi, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        tila.nimi = value
      })
    arvonMuutokset(rivi.kuvaus, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        tila.kuvaus = value
      })
    arvonMuutokset(rivi.vari, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        tila.vari = value
      })
    arvonMuutokset(rivi.oletus, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sarake.tilat.forEach(t => (t.oletus = false))
        for (const muuRivi of this.lomake.tilat) {
          if (muuRivi !== rivi) {
            asetaArvo(muuRivi.oletus, false, { ilmoita: false })
          }
        }
        tila.oletus = value
      })
  }

  private annaUusiTila(): KirjanpitajanSarakkeenTila {
    const uusiTila: KirjanpitajanSarakkeenTila = {
      avain: null,
      kuvaus: '',
      nimi: '',
      vari: null,
      oletus: false,
    }
    return uusiTila
  }

  lisaaTila() {
    const uusiTila = this.annaUusiTila()
    if (!this.sarake.tilat) {
      this.sarake.tilat = []
    }
    this.sarake.tilat.push(uusiTila)
    this._lisaaTilanRivi(uusiTila)
    setTimeout(() => {
      this.tilanNimiInputit.last.focus()
      this._changeDetectorRef.markForCheck()
    }, 10)
  }

  ngOnInit() {
    setTimeout(() => {
      if (this.sarake.aktiivinen) {
        this.sarakkeenNimiInput.focus()
      } else {
        this.sarakeAktiivinenCheckbox.focus()
      }
      this._changeDetectorRef.markForCheck()
    }, 10)
  }

  tallenna() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    return this._kirjanpitajaService
      .tallennaSarake(this._data.kirjanpitajaUid, this.sarake)
      .then(() => {
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close(true)
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }
}
